/**
 * /customer/brands — All Brands page (PHASE W-3 Substep 3d).
 *
 * Sticky chip filter row + responsive brand card grid. Single-select
 * filter: "All" is the default (`selectedCategoryId === null`), tapping
 * the active chip returns to "All". Client-side filter against the
 * already-loaded brand list — instant toggles, no extra round-trip.
 *
 * Card visual is purposefully aligned with the Home "Featured brands"
 * lockup (banner / logo / name / description) and adds a compact
 * per-card category strip when the brand hasn't been joined yet, so
 * customers can scan the taxonomy at a glance.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Tag as TagIcon, Tags, Coffee, IceCream, UtensilsCrossed, Shirt,
  Sparkles, Flower2, BedDouble, Dumbbell, ShoppingBag, Music, HeartPulse,
  GraduationCap, Car, Plane, Briefcase, MoreHorizontal, Pizza, Wine, Cake,
  Camera, Watch, Scissors, Paintbrush, Hammer, Wrench, Heart, Star, MapPin,
  Croissant,
} from 'lucide-react';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

// Curated Lucide map. Mirrors the admin CategoriesPage map so chip icons
// stay consistent across surfaces. Unknown names degrade to `TagIcon`.
const ICONS = {
  Coffee, IceCream, UtensilsCrossed, Shirt, Sparkles, Flower2, BedDouble,
  Dumbbell, ShoppingBag, Music, HeartPulse, GraduationCap, Car, Plane,
  Briefcase, MoreHorizontal, Pizza, Wine, Cake, Camera, Watch, Scissors,
  Paintbrush, Hammer, Wrench, Heart, Star, MapPin, Croissant, Tag: TagIcon,
};
function CategoryIcon({ name, size = 12 }) {
  const C = ICONS[name] ?? TagIcon;
  return <C size={size} />;
}

export default function CustomerBrandsPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  // Single-select chip filter; `null` = "All".
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const brandsQ = useQuery({
    // Keep the cache key stable across chip toggles — we filter
    // client-side, so the underlying brand fetch is invariant.
    queryKey: qk.customerBrands(),
    queryFn: () => api.customerBrands(token),
    enabled: !!token,
  });
  const categoriesQ = useQuery({
    queryKey: qk.categories(),
    queryFn: () => api.categories(token),
    enabled: !!token,
    refetchOnMount: 'always',
  });

  const sortedCategories = useMemo(
    () =>
      [...(categoriesQ.data ?? [])].sort(
        (a, b) => (a.sort_index - b.sort_index) || a.name.localeCompare(b.name),
      ),
    [categoriesQ.data],
  );
  // id → category row for quick per-card chip lookup.
  const catById = useMemo(() => {
    const m = new Map();
    for (const c of sortedCategories) m.set(c.id, c);
    return m;
  }, [sortedCategories]);

  const filteredBrands = useMemo(() => {
    const all = brandsQ.data ?? [];
    if (!selectedCategoryId) return all;
    return all.filter((b) =>
      (b.category_ids ?? []).includes(selectedCategoryId),
    );
  }, [brandsQ.data, selectedCategoryId]);

  const onChip = (id) => {
    setSelectedCategoryId((cur) => (cur === id ? null : id));
  };

  return (
    <div className="space-y-6" data-testid="customer-brands-screen">
      {/* Header + back link */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/customer/home')}
          className="w-9 h-9 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 grid place-items-center text-white/70 hover:text-gold transition-colors"
          aria-label="Back to home"
          data-testid="customer-brands-back"
        >
          <ArrowLeft size={16} />
        </button>
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-gold">
            Explore
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold leading-tight">
            All Brands
          </h1>
        </div>
      </div>

      {/* Sticky chip filter row. `top-14` (mobile) / `top-16` (desktop)
          mirrors CustomerLayout's header height. `-mx-4 md:-mx-8` lets
          the band span the full layout width while the inner scroller
          uses the standard horizontal padding. */}
      <div
        className="sticky top-14 md:top-16 z-20 -mx-4 md:-mx-8 px-4 md:px-8 py-3 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/5"
        data-testid="customer-brands-chip-row"
      >
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          <FilterChip
            testID="customer-brands-chip-all"
            active={selectedCategoryId === null}
            onClick={() => onChip(null)}
          >
            <Tags size={12} /> All
          </FilterChip>
          {categoriesQ.isLoading ? (
            <div className="h-8 w-40 rounded-full bg-white/[0.03] border border-white/5 animate-pulse" />
          ) : null}
          {sortedCategories.map((c) => (
            <FilterChip
              key={c.id}
              testID={`customer-brands-chip-${c.slug}`}
              active={selectedCategoryId === c.id}
              onClick={() => onChip(c.id)}
              color={c.color}
            >
              <CategoryIcon name={c.icon} size={12} />
              {c.name}
            </FilterChip>
          ))}
        </div>
      </div>

      {/* Brand grid */}
      {brandsQ.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-72 rounded-3xl bg-white/[0.03] border border-white/5 animate-pulse"
            />
          ))}
        </div>
      ) : filteredBrands.length === 0 ? (
        <div
          className="rounded-3xl border border-white/8 bg-[#15151B] p-10 text-center"
          data-testid="customer-brands-empty-state"
        >
          <Tags size={28} className="mx-auto text-gold/60 mb-3" />
          <div className="text-sm font-medium">
            No brands in this category yet
          </div>
          <div className="text-xs text-white/45 mt-1">
            Try a different category or tap{' '}
            <button
              type="button"
              className="text-gold underline-offset-2 hover:underline"
              onClick={() => setSelectedCategoryId(null)}
            >
              All
            </button>{' '}
            to see everything.
          </div>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="customer-brands-grid"
        >
          {filteredBrands.map((b) => (
            <BrandCard
              key={b.id}
              brand={b}
              catById={catById}
              onClick={() => navigate(`/customer/brand/${b.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, color, children, testID }) {
  // Active state uses the cohesive gold accent + glow used elsewhere.
  // Inactive chips render on dark surface with subtle border.
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testID}
      className={
        'shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ' +
        (active
          ? 'bg-gold/15 border-gold/45 text-gold shadow-glow-gold-sm'
          : 'bg-[#15151B] border-white/10 text-white/70 hover:border-white/20 hover:text-white')
      }
    >
      {color && !active ? (
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: color }}
        />
      ) : null}
      {children}
    </button>
  );
}

function BrandCard({ brand, catById, onClick }) {
  const cats = (brand.category_ids ?? [])
    .map((id) => catById.get(id))
    .filter(Boolean);
  const visible = cats.slice(0, 2);
  const more = cats.length - visible.length;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`customer-brands-card-${brand.id}`}
      className="text-left rounded-3xl bg-[#15151B] border border-white/8 hover:border-gold/30 transition-all hover:shadow-glow-gold-sm overflow-hidden group"
    >
      <div
        className="h-32 relative bg-gradient-to-br from-gold/15 to-black/40"
        style={
          brand.banner_url
            ? {
                backgroundImage: `url(${resolveImageUrl(brand.banner_url)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : null
        }
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        {brand.logo_url ? (
          <img
            src={resolveImageUrl(brand.logo_url)}
            alt=""
            className="absolute bottom-3 left-3 w-14 h-14 rounded-2xl border border-white/15 shadow-xl bg-black/40"
          />
        ) : null}
      </div>
      <div className="px-4 py-3 space-y-2">
        <div>
          <div className="font-semibold text-sm truncate">{brand.name}</div>
          <div className="text-xs text-white/45 truncate mt-0.5">
            {brand.branch_count ?? 0} location
            {(brand.branch_count ?? 0) === 1 ? '' : 's'}
            {brand.description ? ` · ${brand.description}` : ''}
          </div>
        </div>
        {cats.length > 0 ? (
          <div className="flex items-center gap-1.5 flex-wrap">
            {visible.map((c) => {
              const color = c.color || '#9CA3AF';
              return (
                <span
                  key={c.id}
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
                  style={{
                    color,
                    backgroundColor: `${color}22`,
                    border: `1px solid ${color}55`,
                  }}
                >
                  <CategoryIcon name={c.icon} size={10} />
                  {c.name}
                </span>
              );
            })}
            {more > 0 ? (
              <span className="text-[10px] font-mono text-white/45 px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10">
                +{more}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </button>
  );
}
